import { reactive, readonly, watch, computed } from 'vue'

/**
 * Глобальное состояние темы для админ-панели.
 *
 * Поддерживаются ТРИ варианта оформления:
 *
 *   - `'classic-dark'` — исходный «чёрно-серый» стиль с синим акцентом.
 *   - `'lofi-light'`   — тёплая бумага кафе: кремовый фон + пыльная роза.
 *   - `'lofi-dark'`    — ночь Lofi Girl: плам-фиолет + терракотовый акцент.
 *
 * На `<html>` выставляется одновременно два маркера:
 *
 *   - класс `dark` — определяет color-scheme и активирует `dark:` варианты
 *     Tailwind. Включён для `classic-dark` и `lofi-dark`, выключён для
 *     `lofi-light`.
 *   - атрибут `data-style` со значением `classic` или `lofi` — выбирает
 *     набор цветов в `assets/main.css` (классическая палитра по умолчанию,
 *     `data-style="lofi"` подмешивает тёплые оттенки поверх).
 *
 * Anti-flash bootstrap живёт в `index.html` (выставляет оба маркера ДО
 * монтирования Vue) — здесь только реактивный API и переключатель.
 *
 * Хранилище — `localStorage('admin.theme')`. Для обратной совместимости
 * читаем и старые значения `'light' | 'dark' | 'auto'`.
 */

export type ThemeMode = 'classic-dark' | 'lofi-light' | 'lofi-dark'

const STORAGE_KEY = 'admin.theme'

const MODES: readonly ThemeMode[] = ['classic-dark', 'lofi-light', 'lofi-dark']

/** Описание режима для UI (иконка/подпись подбираются во вьюхах). */
export const THEME_LABEL: Record<ThemeMode, string> = {
  'classic-dark': 'Классическая тёмная',
  'lofi-light': 'Lofi светлая',
  'lofi-dark': 'Lofi тёмная',
}

function readStored(): ThemeMode {
  let raw: string | null = null
  try {
    raw = localStorage.getItem(STORAGE_KEY)
  } catch {
    /* private mode — fallthrough */
  }
  if (raw === 'classic-dark' || raw === 'lofi-light' || raw === 'lofi-dark') return raw

  // Миграция старых значений (`'light' | 'dark' | 'auto'`).
  // `dark` → пользователь явно выбирал тёмную → даём оригинальную «классику»
  //   (это поведение «как было до lofi»).
  // `light` → светлая lofi (классической светлой больше нет).
  // `auto` → ориентируемся на текущие системные предпочтения.
  if (raw === 'dark') return 'classic-dark'
  if (raw === 'light') return 'lofi-light'
  if (raw === 'auto') return resolveAutoLofi()

  // По умолчанию — lofi-light: уютная тёплая бумага.
  return 'lofi-light'
}

function resolveAutoLofi(): 'lofi-light' | 'lofi-dark' {
  try {
    const tg = (window as any).Telegram?.WebApp
    if (tg && (tg.colorScheme === 'dark' || tg.colorScheme === 'light')) {
      return tg.colorScheme === 'dark' ? 'lofi-dark' : 'lofi-light'
    }
  } catch {
    /* SDK мог не подгрузиться */
  }
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'lofi-dark' : 'lofi-light'
}

const THEME_COLOR: Record<ThemeMode, string> = {
  'classic-dark': '#000000',
  'lofi-light': '#fbf4e6',
  'lofi-dark': '#13101c',
}

function applyDocument(mode: ThemeMode): void {
  const html = document.documentElement
  const isDark = mode === 'classic-dark' || mode === 'lofi-dark'
  const style = mode === 'classic-dark' ? 'classic' : 'lofi'

  html.classList.toggle('dark', isDark)
  html.setAttribute('data-style', style)
  html.style.colorScheme = isDark ? 'dark' : 'light'

  const meta = document.getElementById('theme-color-meta')
  if (meta) meta.setAttribute('content', THEME_COLOR[mode])
}

const state = reactive<{ mode: ThemeMode }>({
  mode: readStored(),
})

applyDocument(state.mode)

watch(
  () => state.mode,
  (mode) => {
    try {
      localStorage.setItem(STORAGE_KEY, mode)
    } catch {
      /* noop */
    }
    applyDocument(mode)
  },
)

function setMode(mode: ThemeMode): void {
  state.mode = mode
}

function toggle(): void {
  const idx = MODES.indexOf(state.mode)
  const next = MODES[(idx + 1) % MODES.length]
  state.mode = next
}

/** Совместимость со старым `state.effective` (light/dark) для других частей. */
const effective = computed<'light' | 'dark'>(() =>
  state.mode === 'lofi-light' ? 'light' : 'dark',
)

/** Совместимость со старым `state.style` (classic/lofi). */
const style = computed<'classic' | 'lofi'>(() =>
  state.mode === 'classic-dark' ? 'classic' : 'lofi',
)

export function useTheme() {
  return {
    state: readonly(state),
    effective,
    style,
    modes: MODES,
    setMode,
    toggle,
  }
}
